import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useContent } from "@/context/ContentContext";
import { resolveImage } from "@/lib/api";
import { ShoppingCart, User, LogOut, Menu, Search, ShieldCheck, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useEffect, useState } from "react";
import api from "@/lib/api";

export default function Header() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const { content } = useContent();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)); }, []);

  const onSearch = (e) => {
    e.preventDefault();
    if (search.trim()) navigate(`/products?q=${encodeURIComponent(search.trim())}`);
  };

  const brand = content?.brand || {};
  const ribbon = content?.top_ribbon || "";
  const logo = brand.logo_url;

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
      {/* Top ribbon */}
      <div className="bg-tmf-navy text-white text-xs">
        <div className="max-w-7xl mx-auto px-4 py-2 flex justify-between items-center font-mono-tmf">
          <span className="uppercase tracking-widest truncate">{ribbon}</span>
          <span className="hidden md:flex items-center gap-2 shrink-0">
            <ShieldCheck size={14} className="text-tmf-sky" />
            <span>ISO 13485 · 9001 · CE Certified · GeM OEM</span>
          </span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 h-24 flex items-center gap-6">
        {/* Logo */}
        <Link to="/" data-testid="header-logo" className="flex items-center gap-4 shrink-0 group">
          <div className="w-16 h-16 rounded-full overflow-hidden bg-white border-2 border-slate-200 group-hover:border-tmf-blue transition-colors shadow-sm">
            {logo ? (
              <img src={resolveImage(logo)} alt="TMF" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-tmf-blue text-white font-display font-black text-2xl">T</div>
            )}
          </div>
          <div className="hidden sm:block">
            <div className="font-display font-black text-2xl tracking-tight leading-none text-tmf-navy">{brand.name || "TMF"}</div>
            <div className="text-xs font-mono-tmf tracking-widest text-slate-500 uppercase mt-1">{brand.tagline || "by Silver Sure"}</div>
          </div>
        </Link>

        {/* Nav */}
        <nav className="hidden lg:flex items-center gap-7 text-base font-medium">
          {/* All Products dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger data-testid="nav-all-products" className="flex items-center gap-1 text-tmf-navy hover:text-tmf-blue transition-colors outline-none">
              All Products <ChevronDown size={14} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="rounded-none border border-slate-300 w-72 p-0">
              <Link
                to="/products"
                data-testid="dropdown-all-products"
                className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 border-b border-slate-200 font-display font-bold uppercase tracking-tight text-sm text-tmf-navy"
              >
                All Products
                <span className="text-[10px] font-mono-tmf text-slate-500">Browse</span>
              </Link>
              <div className="grid">
                {categories.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/category/${c.slug}`}
                    data-testid={`dropdown-${c.slug}`}
                    className="flex items-center justify-between px-4 py-2.5 hover:bg-slate-50 hover:text-tmf-blue border-b border-slate-100 last:border-b-0 text-sm"
                  >
                    <span className="font-medium">{c.name}</span>
                    <span className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-400">{c.description}</span>
                  </Link>
                ))}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          <Link to="/about" data-testid="nav-about" className="text-tmf-navy hover:text-tmf-blue transition-colors">About</Link>
          <Link to="/contact" data-testid="nav-contact" className="text-tmf-navy hover:text-tmf-blue transition-colors">Contact</Link>
        </nav>

        {/* Search */}
        <form onSubmit={onSearch} className="hidden md:flex flex-1 max-w-md ml-auto">
          <div className="relative w-full">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="header-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search hospital furniture..."
              className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-tmf-blue focus:border-tmf-blue bg-white"
            />
          </div>
        </form>

        {/* Actions */}
        <div className="flex items-center gap-2 ml-auto lg:ml-0">
          <Link to="/cart" data-testid="header-cart" className="relative p-2 hover:bg-slate-100 transition-colors">
            <ShoppingCart size={20} className="text-tmf-navy" />
            {count > 0 && (
              <span className="absolute -top-1 -right-1 bg-tmf-orange text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full">
                {count}
              </span>
            )}
          </Link>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="header-user-menu" className="p-2 hover:bg-slate-100 transition-colors">
                <User size={20} className="text-tmf-navy" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-none border border-slate-300 w-56">
                <div className="px-3 py-2 border-b border-slate-200">
                  <div className="text-sm font-bold">{user.name}</div>
                  <div className="text-xs text-slate-500">{user.email}</div>
                </div>
                <DropdownMenuItem asChild>
                  <Link to="/account" data-testid="menu-account" className="cursor-pointer">My Account</Link>
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem asChild>
                    <Link to="/admin" data-testid="menu-admin" className="cursor-pointer">Admin Dashboard</Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="menu-logout"
                  onClick={async () => { await logout(); navigate("/"); }}
                  className="cursor-pointer text-tmf-orange"
                >
                  <LogOut size={14} className="mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button
                asChild
                variant="outline"
                data-testid="header-login-btn"
                className="rounded-none border-tmf-navy text-tmf-navy hover:bg-tmf-navy hover:text-white hidden sm:inline-flex"
              >
                <Link to="/login">Login</Link>
              </Button>
              <Button
                asChild
                data-testid="header-register-btn"
                className="rounded-none bg-tmf-blue hover:bg-tmf-navy text-white hidden sm:inline-flex"
              >
                <Link to="/register">Sign Up</Link>
              </Button>
            </>
          )}

          {/* Mobile menu */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-btn" className="lg:hidden p-2">
                <Menu size={20} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 rounded-none">
              <div className="flex flex-col gap-1 mt-8">
                <Link to="/products" onClick={() => setMobileOpen(false)} className="text-lg font-display font-bold uppercase tracking-tight py-2 hover:text-tmf-blue">All Products</Link>
                {categories.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/category/${c.slug}`}
                    onClick={() => setMobileOpen(false)}
                    className="text-sm py-1.5 pl-3 border-l-2 border-slate-200 hover:border-tmf-blue hover:text-tmf-blue"
                  >
                    {c.name}
                  </Link>
                ))}
                <Link to="/about" onClick={() => setMobileOpen(false)} className="text-lg font-display font-bold uppercase tracking-tight py-2 mt-3 hover:text-tmf-blue">About</Link>
                <Link to="/contact" onClick={() => setMobileOpen(false)} className="text-lg font-display font-bold uppercase tracking-tight py-2 hover:text-tmf-blue">Contact</Link>
                {!user && (
                  <div className="flex gap-2 mt-4">
                    <Button asChild variant="outline" className="rounded-none flex-1"><Link to="/login">Login</Link></Button>
                    <Button asChild className="rounded-none flex-1 bg-tmf-blue hover:bg-tmf-navy"><Link to="/register">Sign Up</Link></Button>
                  </div>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
